import { useEffect, useState } from "react";
import api, { errMsg } from "../lib/api";
import Modal, { Field } from "../components/Modal";
import { useAuth } from "../context/AuthContext";
import { Plus, Trash2, Shield, User, Crown, KeyRound } from "lucide-react";
import { toast } from "sonner";

const PERM_LABELS = {
  stok: "Lihat Menu Stok",
  stok_edit: "Edit Stok (Tambah/Edit/Ubah Stok & Harga)",
  prices: "Lihat Daftar Harga",
  modal: "Lihat HPP / Modal",
  mutasi: "Buka Mutasi Stok",
  mutasi_edit: "Edit / Hapus Mutasi Stok",
  penjualan: "Buka Penjualan",
  penjualan_edit: "Edit / Cancel Penjualan",
  hutang: "Buka Menu Hutang",
  absensi: "Buka Absensi",
  laporan: "Buka Laporan",
  monthly: "Buka Laporan Bulanan",
  rusak: "Buka Barang Rusak",
  keuangan: "Lihat Data Keuangan (Omzet & Profit)",
};
const emptyPerms = { stok: false, stok_edit: false, prices: false, modal: false, mutasi: false, mutasi_edit: false, penjualan: false, penjualan_edit: false, hutang: false, absensi: false, laporan: false, monthly: false, rusak: false, keuangan: false };

export default function Users() {
  const { user } = useAuth();
  const [users, setUsers] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", password: "", role: "staff", perms: { ...emptyPerms } });
  const [pwOpen, setPwOpen] = useState(false);
  const [pwTarget, setPwTarget] = useState(null);
  const [pwVal, setPwVal] = useState("");

  const load = () => api.get("/users").then((r) => setUsers(r.data)).catch((e) => toast.error(errMsg(e)));
  useEffect(() => { load(); }, []);

  const save = async (e) => {
    e.preventDefault();
    try {
      await api.post("/auth/register", form);
      toast.success("Pengguna ditambahkan");
      setOpen(false);
      setForm({ name: "", email: "", password: "", role: "staff", perms: { ...emptyPerms } });
      load();
    } catch (err) { toast.error(errMsg(err)); }
  };

  const remove = async (id) => {
    if (!window.confirm("Hapus pengguna ini?")) return;
    try { await api.delete(`/users/${id}`); toast.success("Pengguna dihapus"); load(); }
    catch (err) { toast.error(errMsg(err)); }
  };

  const submitPw = async (e) => {
    e.preventDefault();
    try {
      await api.put(`/users/${pwTarget.id}/password`, { password: pwVal });
      toast.success("Password diperbarui");
      setPwOpen(false); setPwVal("");
    } catch (err) { toast.error(errMsg(err)); }
  };

  const togglePerm = async (u, key) => {
    const next = { ...emptyPerms, ...u.perms, [key]: !u.perms?.[key] };
    try {
      await api.put(`/users/${u.id}/perms`, next);
      setUsers((list) => list.map((x) => (x.id === u.id ? { ...x, perms: next } : x)));
      toast.success("Akses diperbarui");
    } catch (err) { toast.error(errMsg(err)); }
  };

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const setPerm = (k) => (e) => setForm({ ...form, perms: { ...form.perms, [k]: e.target.checked } });

  return (
    <div className="space-y-5 animate-fadeup">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <h1 className="text-2xl sm:text-3xl font-display font-extrabold">Pengguna</h1>
          <p className="text-[#52525B] text-sm">Kelola akun & akses (khusus Owner)</p>
        </div>
        <button data-testid="add-user-button" onClick={() => setOpen(true)} className="nb-btn px-4 py-2.5 flex items-center gap-2 text-[#0A0A0A]" style={{ background: "var(--primary)" }}>
          <Plus className="w-5 h-5" strokeWidth={2.5} /> Tambah Pengguna
        </button>
      </div>

      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 items-start">
        {users.map((u) => (
          <div key={u.id} data-testid={`user-card-${u.id}`} className="nb-card p-4">
            <div className="flex items-center gap-3">
              <div className="w-11 h-11 border-2 border-[#0A0A0A] rounded-lg flex items-center justify-center shrink-0" style={{ background: u.is_owner ? "var(--primary)" : u.role === "admin" ? "var(--masuk)" : "#fff" }}>
                {u.is_owner ? <Crown className="w-5 h-5" strokeWidth={2.4} /> : u.role === "admin" ? <Shield className="w-5 h-5" strokeWidth={2.4} /> : <User className="w-5 h-5" strokeWidth={2.4} />}
              </div>
              <div className="flex-1 min-w-0">
                <div className="font-bold truncate">{u.name}</div>
                <div className="text-xs text-[#52525B] truncate">{u.email}</div>
                <span className="label-tag">{u.is_owner ? "owner" : u.role}</span>
              </div>
              {!u.is_owner && (
                <div className="flex gap-1 shrink-0">
                  <button data-testid={`reset-pw-${u.id}`} onClick={() => { setPwTarget(u); setPwVal(""); setPwOpen(true); }} title="Reset password" className="w-8 h-8 border-2 border-[#0A0A0A] rounded-lg flex items-center justify-center hover:bg-[#FAF9F6]"><KeyRound className="w-4 h-4" /></button>
                  {u.id !== user.id && (
                    <button data-testid={`delete-user-${u.id}`} onClick={() => remove(u.id)} className="w-8 h-8 border-2 border-[#0A0A0A] rounded-lg flex items-center justify-center" style={{ color: "var(--retur)" }}><Trash2 className="w-4 h-4" /></button>
                  )}
                </div>
              )}
            </div>

            {u.is_owner ? (
              <p className="text-xs text-[#52525B] mt-3">Owner punya akses penuh ke semua fitur.</p>
            ) : (
              <div className="mt-3 space-y-1.5 border-t-2 border-[#0A0A0A]/10 pt-3">
                <div className="label-tag mb-1">Akses Kolom / Menu</div>
                {Object.keys(PERM_LABELS).map((k) => (
                  <label key={k} className="flex items-center gap-2 text-sm font-semibold cursor-pointer">
                    <input type="checkbox" data-testid={`perm-${k}-${u.id}`} checked={!!u.perms?.[k]} onChange={() => togglePerm(u, k)} className="w-4 h-4 accent-[#E9C46A]" />
                    {PERM_LABELS[k]}
                  </label>
                ))}
              </div>
            )}
          </div>
        ))}
      </div>

      <Modal open={open} onClose={() => setOpen(false)} title="Tambah Pengguna" testid="user-modal">
        <form onSubmit={save}>
          <Field label="Nama"><input data-testid="user-name" required value={form.name} onChange={set("name")} className="nb-input" /></Field>
          <Field label="Email"><input data-testid="user-email" type="email" required value={form.email} onChange={set("email")} className="nb-input" /></Field>
          <Field label="Password"><input data-testid="user-password" type="password" required value={form.password} onChange={set("password")} className="nb-input" /></Field>
          <Field label="Peran">
            <select data-testid="user-role" value={form.role} onChange={set("role")} className="nb-input">
              <option value="staff">Staff</option>
              <option value="admin">Admin</option>
            </select>
          </Field>
          <Field label="Akses Kolom / Menu">
            <div className="space-y-1.5">
              {Object.keys(PERM_LABELS).map((k) => (
                <label key={k} className="flex items-center gap-2 text-sm font-semibold cursor-pointer">
                  <input type="checkbox" data-testid={`form-perm-${k}`} checked={form.perms[k]} onChange={setPerm(k)} className="w-4 h-4 accent-[#E9C46A]" />
                  {PERM_LABELS[k]}
                </label>
              ))}
            </div>
          </Field>
          <button data-testid="save-user-button" type="submit" className="nb-btn w-full py-3 mt-1 text-[#0A0A0A]" style={{ background: "var(--primary)" }}>Simpan</button>
        </form>
      </Modal>

      <Modal open={pwOpen} onClose={() => setPwOpen(false)} title={`Reset Password — ${pwTarget?.name || ""}`} testid="password-modal">
        <form onSubmit={submitPw}>
          <Field label="Password Baru"><input data-testid="new-password-input" type="text" required minLength={4} value={pwVal} onChange={(e) => setPwVal(e.target.value)} className="nb-input" placeholder="Ketik password baru untuk user ini" /></Field>
          <button data-testid="save-password-button" type="submit" className="nb-btn w-full py-3 mt-1 text-[#0A0A0A]" style={{ background: "var(--primary)" }}>Simpan Password</button>
        </form>
      </Modal>
    </div>
  );
}
